import { create } from 'zustand';
import { Workout } from '@/types/workout';
import { presetWorkouts } from '@/data/presetWorkouts';
import { workoutApi } from '@/services/workoutApi';
import { useAuthStore } from './authStore';

// Track active request promise to prevent duplicate concurrent calls
let activeLoadPromise: Promise<void> | null = null;

interface WorkoutStore {
  presetWorkouts: Workout[];
  generatedWorkouts: Workout[];
  activeWorkout: Workout | null;
  loading: boolean;
  error: string | null;
  
  // Actions
  loadPresets: () => void;
  loadGeneratedWorkouts: () => Promise<void>;
  addGeneratedWorkout: (workout: Workout) => Promise<void>;
  updateWorkout: (workout: Workout) => Promise<void>;
  setActiveWorkout: (workout: Workout | null) => void;
  deleteWorkout: (workoutId: string) => Promise<void>;
  refreshWorkouts: () => Promise<void>;
}

export const useWorkoutStore = create<WorkoutStore>((set, get) => ({
  presetWorkouts: [],
  generatedWorkouts: [],
  activeWorkout: null,
  loading: false,
  error: null,

  loadPresets: () => {
    set({ presetWorkouts });
  },

  loadGeneratedWorkouts: async () => {
    // If a request is already in progress, return the existing promise
    if (activeLoadPromise) {
      return activeLoadPromise;
    }

    // Only load workouts if user is authenticated
    const { isAuthenticated } = useAuthStore.getState();
    if (!isAuthenticated) {
      set({ generatedWorkouts: [], loading: false });
      return;
    }

    // Create the request promise
    activeLoadPromise = (async () => {
      set({ loading: true, error: null });
      try {
        const workouts = await workoutApi.getWorkouts();
        const generated = workouts.filter(w => !w.is_preset);
        set({ generatedWorkouts: generated, loading: false });
      } catch (error) {
        console.error('Failed to load workouts:', error);
        set({ 
          error: error instanceof Error ? error.message : 'Failed to load workouts',
          loading: false 
        });
      } finally {
        // Clear the active promise when done
        activeLoadPromise = null;
      }
    })();

    return activeLoadPromise;
  },

  addGeneratedWorkout: async (workout: Workout) => {
    // Only create workout if user is authenticated
    const { isAuthenticated } = useAuthStore.getState();
    if (!isAuthenticated) {
      throw new Error('User must be authenticated to create workouts');
    }

    set({ loading: true, error: null });
    try {
      const createdWorkout = await workoutApi.createWorkout(workout);
      set(state => ({
        generatedWorkouts: [...state.generatedWorkouts, createdWorkout],
        loading: false,
      }));
    } catch (error) {
      console.error('Failed to create workout:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to create workout',
        loading: false 
      });
      throw error;
    }
  },

  updateWorkout: async (workout: Workout) => {
    set({ loading: true, error: null });
    try {
      const updatedWorkout = await workoutApi.updateWorkout(workout.id, workout);
      set(state => ({
        generatedWorkouts: state.generatedWorkouts.map(w =>
          w.id === workout.id ? updatedWorkout : w
        ),
        activeWorkout: state.activeWorkout?.id === workout.id ? updatedWorkout : state.activeWorkout,
        loading: false,
      }));
    } catch (error) {
      console.error('Failed to update workout:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to update workout',
        loading: false 
      });
      throw error;
    }
  },

  setActiveWorkout: (workout: Workout | null) => {
    set({ activeWorkout: workout });
  },

  deleteWorkout: async (workoutId: string) => {
    set({ loading: true, error: null });
    try {
      await workoutApi.deleteWorkout(workoutId);
      set(state => ({
        generatedWorkouts: state.generatedWorkouts.filter(w => w.id !== workoutId),
        activeWorkout: state.activeWorkout?.id === workoutId ? null : state.activeWorkout,
        loading: false,
      }));
    } catch (error) {
      console.error('Failed to delete workout:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to delete workout',
        loading: false 
      });
      throw error;
    }
  },

  refreshWorkouts: async () => {
    await get().loadGeneratedWorkouts();
  },
}));

// Listen to auth state changes and clear workouts when user signs out
useAuthStore.subscribe(
  (state) => state.isAuthenticated,
  (isAuthenticated) => {
    if (!isAuthenticated) {
      // Clear workouts when user signs out
      useWorkoutStore.setState({ generatedWorkouts: [] });
    }
  }
);

